'use client';

import { AppBar, AppBarButtonNavigation, cn, IconButton } from '@bridebook/aura';
import { ChevronLeft, Close } from '@bridebook/aura/src/icons';
import { RoundedModalCloseButton } from '../rounded-modal/rounded-modal-header';
import {
  ModalControlContext,
  ModalControlContextType,
  useModalControlContext,
} from '../modal-control-context';
import { DialogTitle } from '../dialog-title';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { ComponentPropsWithoutRef, ElementRef, forwardRef, useMemo } from 'react';

export type FullPageModalProps = Omit<
  ComponentPropsWithoutRef<typeof DialogPrimitive.Root>,
  'open'
> & {
  isOpen: boolean;
  className?: string;
  /** Function to force close the modal bypassing onOpenChange logic.
   * Usefull for example when you need to close the modal at the end of some flow (like long form) and bypass the regular onOpenChange logic where you might have some analytics of "are you sure you want to close?" etc. */
  forceClose?: () => void;
};

export const FullPageModal = forwardRef<
  ElementRef<typeof DialogPrimitive.Content>,
  FullPageModalProps
>(({ children, isOpen, onOpenChange, className, forceClose, ...props }, ref) => {
  const value = useMemo(
    () =>
      ({
        isOpen,
        onOpenChange,
        close: () => onOpenChange?.(false),
        forceClose,
      }) satisfies ModalControlContextType,
    [isOpen, onOpenChange, forceClose],
  );
  return (
    <ModalControlContext.Provider value={value}>
      <DialogPrimitive.Root open={isOpen} onOpenChange={onOpenChange} {...props}>
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay />
          <DialogPrimitive.Content
            aria-describedby={undefined}
            ref={ref}
            data-slot="dialog-content"
            className={cn(
              'fixed inset-0 z-[10000] flex max-w-full flex-col bg-white pt-safe-area-inset-top pb-safe-area-inset-bottom',
              className,
            )}
            {...props}>
            {children}
          </DialogPrimitive.Content>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
    </ModalControlContext.Provider>
  );
});
FullPageModal.displayName = 'FullPageModal';

type FullPageModalHeaderProps = Omit<ComponentPropsWithoutRef<typeof AppBar>, 'header'> & {
  showCloseButton?: boolean;
  closeButtonProps?: ComponentPropsWithoutRef<typeof RoundedModalCloseButton>;
  onBackButtonClick?: () => void;
  backButtonProps?: ComponentPropsWithoutRef<typeof IconButton>;
  appBarProps?: ComponentPropsWithoutRef<typeof AppBar>;
};

export const FullPageModalHeader = ({
  className,
  children,
  showCloseButton = true,
  closeButtonProps,
  onBackButtonClick,
  backButtonProps,
  ...appBarProps
}: FullPageModalHeaderProps) => (
  <>
    <AppBar
      className={cn(className)}
      leftElement={
        onBackButtonClick && (
          <AppBarButtonNavigation
            icon={ChevronLeft}
            onClick={onBackButtonClick}
            {...backButtonProps}
          />
        )
      }
      header={
        <div className="min-w-0 truncate text-center typo-base-semibold text-space">{children}</div>
      }
      rightElement={showCloseButton && <FullPageModalCloseButton {...closeButtonProps} />}
      {...appBarProps}
    />
    {/* Visually hidden title for screen readers, and to fix radix dialog warnings */}
    <VisuallyHidden>
      <DialogTitle>{children}</DialogTitle>
    </VisuallyHidden>
  </>
);

export const FullPageModalContent = ({
  className,
  children,
  ...props
}: ComponentPropsWithoutRef<typeof DialogPrimitive.Content>) => (
  <div
    data-slot="modal-content"
    className={cn('flex flex-1 flex-col overflow-y-auto p-4 scrollbar-thin', className)}
    {...props}>
    {children}
  </div>
);

export const FullPageModalFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    data-slot="dialog-footer"
    className={cn('flex justify-center gap-3 border-t border-space-15 bg-white p-4', className)}
    {...props}
  />
);

export const FullPageModalCloseButton = ({
  className,
  ...props
}: Omit<ComponentPropsWithoutRef<typeof IconButton>, 'icon'>) => {
  const { close } = useModalControlContext();
  return <AppBarButtonNavigation icon={Close} onClick={close} className={className} {...props} />;
};
